import { agpAxiosClient } from '../../Common/api/ABHub/ABHubTokenConfig';
import {
    Version,
    VersionResponse,
    AGCPPublishResponse,
} from '../models/AirGapMessage';
import { CancelToken } from 'axios';
import { RequestResult } from '../../AGCP/models/AirGapMessage';
import {
    EnvironmentData,
    EnvironmentDataResponse,
    EnvironmentListResponse,
    HeartbeatUptimeApiResponse,
} from '../models/EnvironmentModels';

import {
    getConfig,
    GlobalConfigProperties,
} from '../../Common/api/ABHub/ABHubGlobalConfig';
import { ReplayByIdsResult } from '../models/ReplayByIdsResult';

export const getVersion = async (
    cancelToken?: CancelToken
): Promise<Version[]> => {
    const agssUrl = getConfig(GlobalConfigProperties.AgssUrl);
    const response = await agpAxiosClient.get<VersionResponse>(
        agssUrl + '/api/version',
        { cancelToken }
    );

    return response.data.versions;
};

export const publishCommand = async (
    message: any,
    eventType: string,
    environment: string,
    cancelToken?: CancelToken
): Promise<AGCPPublishResponse<boolean>> => {
    const response = await agpAxiosClient.post(
        `/API/publish/${eventType}/${environment}`,
        message,
        {
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    return response.data;
};

export const reqeueDeadLetterMessages = async (
    queueName: string,
    messageCount: number,
    region?: 'primary' | 'secondary',
    cancelToken?: CancelToken
): Promise<RequestResult<number>> => {
    if (messageCount > 1000) {
        messageCount = 1000;
    }

    let agcpUrl: string;
    if (region === 'primary') {
        agcpUrl = getConfig(GlobalConfigProperties.AgcpPrimaryUrl);
    } else if (region === 'secondary') {
        agcpUrl = getConfig(GlobalConfigProperties.AgcpSecondaryUrl);
    } else {
        agcpUrl = getConfig(GlobalConfigProperties.AgcpUrl);
    }

    const response = await agpAxiosClient.post<RequestResult<number>>(
        `${agcpUrl}/api/maintenance/requeuedeadletter`,
        {}, // No body data to send for this request
        {
            params: {
                queueName,
                messageCount,
            },
            cancelToken: cancelToken,
        }
    );

    return response.data;
};

export const replayMessagesByIds = async (
    ids: string[],
    cancelToken?: CancelToken
): Promise<RequestResult<ReplayByIdsResult>> => {
    // Use the AGCP traffic manager base URL (region-agnostic).
    const agcpUrl = getConfig(GlobalConfigProperties.AgcpUrl);

    // The replayByIds endpoint expects a raw JSON array of message ids in the body.
    const response = await agpAxiosClient.post<
        RequestResult<ReplayByIdsResult>
    >(`${agcpUrl}/api/maintenance/replayByIds`, ids, {
        headers: {
            'Content-Type': 'application/json',
        },
        cancelToken: cancelToken,
    });

    return response.data;
};

export const getEnvironment = async (
    name: string,
    cancelToken?: CancelToken
): Promise<EnvironmentDataResponse> => {
    const agcpUrl = getConfig(GlobalConfigProperties.AgcpUrl);

    const response = await agpAxiosClient.get<EnvironmentDataResponse>(
        `${agcpUrl}/api/environment`,
        {
            params: { name },
            cancelToken: cancelToken,
        }
    );

    return response.data;
};

export const getEnvironmentList = async (
    cancelToken?: CancelToken
): Promise<EnvironmentListResponse> => {
    const agcpUrl = getConfig(GlobalConfigProperties.AgcpUrl);

    const response = await agpAxiosClient.get<EnvironmentListResponse>(
        `${agcpUrl}/api/environment/list`,
        {
            cancelToken: cancelToken,
        }
    );

    return response.data;
};

/**
 * Get the SRMP messaging heartbeat round-trip uptime bars (24h + 7d) for every cloud+region pair at once
 * @param cancelToken Optional axios cancel token
 * @returns Promise containing heartbeat uptime data
 */
export const getHeartbeatUptime = async (
    cancelToken?: CancelToken
): Promise<HeartbeatUptimeApiResponse> => {
    const agcpUrl = getConfig(GlobalConfigProperties.AgcpUrl);

    const response = await agpAxiosClient.get<HeartbeatUptimeApiResponse>(
        `${agcpUrl}/api/srmp-heartbeat-uptime`,
        {
            cancelToken: cancelToken,
        }
    );

    return response.data;
};

export const upsertEnvironment = async (
    environmentData: EnvironmentData,
    cancelToken?: CancelToken
): Promise<EnvironmentDataResponse> => {
    const agcpUrl = getConfig(GlobalConfigProperties.AgcpUrl);

    const response = await agpAxiosClient.post<EnvironmentDataResponse>(
        `${agcpUrl}/api/environment`,
        environmentData,
        {
            cancelToken: cancelToken,
        }
    );

    return response.data;
};
